import { create } from 'zustand';

import {
  initialWindowMode,
  type MobileAnchor,
  type WindowCorner,
  type WindowMode,
} from '#features/media-window/model';

export type VoiceConnectionPhase = 'idle' | 'connecting' | 'connected' | 'reconnecting';

const PUSH_TO_TALK_KEY = 'voice.pushToTalk';

/**
 * Режим рации — выбор человека, а не свойство разговора, поэтому переживает выход из комнаты и
 * перезагрузку страницы. Иначе тот, кому рация нужна (открытый микрофон в общей комнате слышен
 * всем домашним), включал бы её каждый раз заново.
 *
 * По умолчанию выключен: заходишь и говоришь, как в Meet. Хранилище бывает запрещено настройками
 * сайта — тогда просто работает умолчание.
 */
const readPushToTalk = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(PUSH_TO_TALK_KEY) === '1';
  } catch {
    return false;
  }
};

const writePushToTalk = (enabled: boolean) => {
  try {
    globalThis.localStorage?.setItem(PUSH_TO_TALK_KEY, enabled ? '1' : '0');
  } catch {
    // Запрет хранилища не повод ломать переключатель: в этом разговоре он всё равно работает.
  }
};

/**
 * Как показан виджет разговора: свёрнутая пилюля, плавающее окно или весь экран. Те же три
 * состояния, что у звонка 1:1 — окно одно и то же, и вести себя оно обязано одинаково.
 */
export type VoiceWindowMode = WindowMode;

export type VoiceRoomInfo = {
  sessionId: string;
  postId: string;
  hostId: string | null;
  livekitUrl: string;
  token: string;
  maxParticipants: number | null;
  /**
   * Игра и хозяин сессии — подпись для плавающего окна. Кладутся сюда в момент входа, потому что
   * окно живёт в корне лейаута и данных поста не видит: тянуть их отдельным запросом ради двух
   * строк подписи незачем.
   */
  gameName?: string | null;
  hostName?: string | null;
};

/**
 * Участник комнаты глазами интерфейса. Присутствие приходит из двух источников: список от сервера
 * (кто вообще относится к комнате) и живое состояние LiveKit (кто подключён и кто говорит прямо
 * сейчас). Второе обновляется десятки раз в секунду, поэтому держится отдельно от серверных данных.
 */
export type VoiceMember = {
  userId: string;
  connected: boolean;
  host: boolean;
  speaking: boolean;
  muted: boolean;
  /**
   * Имя и аватар приходят не с сервера, а из токена LiveKit — там они уже есть у каждого
   * подключённого. Отдельного запроса профилей ради списка на четыре строки не делаем.
   */
  displayName?: string | null;
  avatar?: string | null;
};

/**
 * Качество связи глазами интерфейса. Хорошая связь молчит, нестабильная предупреждает, потерянная
 * уводит виджет в переподключение — трёх состояний хватает, шкала из пяти делений человеку
 * посреди катки ничего не даёт.
 */
export type VoiceQuality = 'unknown' | 'good' | 'unstable' | 'lost';

type VoiceSlice = {
  phase: VoiceConnectionPhase;
  room: VoiceRoomInfo | null;
  members: VoiceMember[];
  micEnabled: boolean;
  /**
   * Микрофон не отдала система, а не выключил человек. Живёт в сторе, а не в хуке комнаты,
   * потому что об этом обязаны знать оба места: и виджет внутри разговора, и карточка сессии —
   * иначе карточка продолжает называть системный запрет собственным выбором игрока.
   */
  micDenied: boolean;
  /** Рация: микрофон открыт только пока держат пробел. Выключено — микрофон открыт всегда. */
  pushToTalk: boolean;
  windowMode: VoiceWindowMode;
  /** Угол экрана на десктопе — окно перетаскивают и оно прилипает к ближайшему. */
  corner: WindowCorner;
  /** Край экрана на телефоне: внизу окно накрывает поле ввода, наверху — освобождает его. */
  mobileAnchor: MobileAnchor;
  quality: VoiceQuality;
  /** Локальная громкость участников, 0–1. Касается только меня — модерация живёт отдельно. */
  localVolumes: Record<string, number>;
  /**
   * Кого я не слышу. Отдельно от громкости, а не как «громкость = 0»: громкость `<audio>` меняется
   * только на десктопе, а `muted` работает везде — на телефоне это единственный способ действительно
   * заглушить человека.
   */
  localMuted: Record<string, boolean>;
  /**
   * Последний отказ: код и сессия, в которую не пустили. Без сессии код был бы глобальным, и
   * «тебя выгнали» с одной карточки показывалось бы на всех остальных.
   */
  lastError: string | null;
  lastErrorSessionId: string | null;

  setConnecting: (room: VoiceRoomInfo) => void;
  setConnected: () => void;
  setReconnecting: () => void;
  setMembers: (members: VoiceMember[]) => void;
  /** Точечное обновление живого состояния — не трогает состав, только флаги. */
  updateLiveState: (state: {
    speaking: string[];
    connected: string[];
    muted?: string[];
    profiles?: Record<string, { displayName?: string | null; avatar?: string | null }>;
  }) => void;
  setMicEnabled: (enabled: boolean) => void;
  setMicDenied: (denied: boolean) => void;
  setPushToTalk: (enabled: boolean) => void;
  setWindowMode: (mode: VoiceWindowMode) => void;
  setCorner: (corner: WindowCorner) => void;
  setMobileAnchor: (anchor: MobileAnchor) => void;
  setQuality: (quality: VoiceQuality) => void;
  setLocalVolume: (userId: string, volume: number) => void;
  setLocalMuted: (userId: string, muted: boolean) => void;
  setError: (code: string | null, sessionId?: string | null) => void;
  reset: () => void;
};

export const useVoiceStore = create<VoiceSlice>()((set, get) => ({
  phase: 'idle',
  room: null,
  members: [],
  micEnabled: true,
  micDenied: false,
  pushToTalk: readPushToTalk(),
  windowMode: 'window',
  corner: 'br',
  mobileAnchor: 'bottom',
  quality: 'unknown',
  localVolumes: {},
  localMuted: {},
  lastError: null,
  lastErrorSessionId: null,

  setConnecting: (room) =>
    set({
      phase: 'connecting',
      room,
      lastError: null,
      lastErrorSessionId: null,
      // В рации разговор начинается с закрытым микрофоном — в этом весь её смысл.
      micEnabled: !get().pushToTalk,
      micDenied: false,
      quality: 'unknown',
      // Каждый разговор начинается с состояния по умолчанию для этого экрана: на телефоне — полоса,
      // на десктопе — окно. Иначе на телефон переезжает раскрытое окно с прошлого раза.
      windowMode: initialWindowMode(),
    }),

  setConnected: () => set({ phase: 'connected' }),

  // Переподключение — состояние SDK, а не наше решение: комната и состав при этом сохраняются,
  // иначе виджет мигал бы пустым списком на каждом провале сети.
  setReconnecting: () => set({ phase: 'reconnecting' }),

  setMembers: (members) => set({ members }),

  updateLiveState: ({ speaking, connected, muted, profiles }) => {
    const speakingSet = new Set(speaking);
    const connectedSet = new Set(connected);
    const mutedSet = new Set(muted ?? []);
    const hostId = get().room?.hostId ?? null;

    const known = get().members.map((member) => {
      const profile = profiles?.[member.userId];
      return {
        ...member,
        speaking: speakingSet.has(member.userId),
        connected: connectedSet.has(member.userId),
        muted: mutedSet.has(member.userId),
        // Имя, добытое однажды, не теряем: отключившийся участник остаётся в списке, и подменять
        // его имя на UUID в момент выхода — худшее, что можно сделать.
        displayName: profile?.displayName ?? member.displayName ?? null,
        avatar: profile?.avatar ?? member.avatar ?? null,
      };
    });

    // Зашедшие ПОСЛЕ меня в списке с сервера отсутствуют по построению: он снят в момент моего
    // входа. Пока их не добавляли здесь, зашедший первым не видел вообще никого и навсегда
    // оставался «1 из N» — при том, что второй его прекрасно видел. Медиасервер знает про каждого
    // подключённого, включая имя и аватар из токена, поэтому достроить строку есть из чего.
    const seen = new Set(known.map((member) => member.userId));
    const arrived = connected
      .filter((userId) => !seen.has(userId))
      .map((userId) => ({
        userId,
        connected: true,
        host: hostId != null && userId === hostId,
        speaking: speakingSet.has(userId),
        muted: mutedSet.has(userId),
        displayName: profiles?.[userId]?.displayName ?? null,
        avatar: profiles?.[userId]?.avatar ?? null,
      }));

    set({ members: arrived.length > 0 ? [...known, ...arrived] : known });
  },

  /**
   * Включение рации закрывает микрофон, выключение — открывает: иначе человек, переключивший
   * режим, остаётся в противоположном состоянии, чем ожидает, и узнаёт об этом от собеседников.
   */
  setPushToTalk: (pushToTalk) => {
    writePushToTalk(pushToTalk);
    set({ pushToTalk, micEnabled: !pushToTalk });
  },

  setMicEnabled: (micEnabled) => set({ micEnabled }),
  setMicDenied: (micDenied) => set({ micDenied }),
  setWindowMode: (windowMode) => set({ windowMode }),
  // Угол переживает выход из голоса намеренно: человек однажды перетащил окно туда, где оно ему
  // не мешает, и следующий разговор обязан открыться там же.
  setCorner: (corner) => set({ corner }),
  // Край на телефоне переживает разговор по той же причине, что и угол на десктопе: человек
  // поднял окно наверх, потому что снизу оно закрывало ему поле ввода, и следующий разговор
  // обязан открыться там же, а не снова поверх клавиатуры.
  setMobileAnchor: (mobileAnchor) => set({ mobileAnchor }),
  setQuality: (quality) => set({ quality }),
  setLocalVolume: (userId, volume) =>
    set({ localVolumes: { ...get().localVolumes, [userId]: volume } }),
  setLocalMuted: (userId, muted) =>
    set({ localMuted: { ...get().localMuted, [userId]: muted } }),
  setError: (lastError, lastErrorSessionId = null) => set({ lastError, lastErrorSessionId }),

  reset: () =>
    set({
      phase: 'idle',
      room: null,
      members: [],
      micEnabled: !get().pushToTalk,
      micDenied: false,
      quality: 'unknown',
      localVolumes: {},
      localMuted: {},
      lastError: null,
      lastErrorSessionId: null,
    }),
}));

/** Мы сейчас в голосе этой сессии? Нужен карточкам, чтобы показать «выйти» вместо «зайти». */
export const useIsInVoiceRoom = (sessionId: string | null | undefined) =>
  useVoiceStore(
    (state) => Boolean(sessionId) && state.room?.sessionId === sessionId && state.phase !== 'idle'
  );
